import { theme } from 'antd';
import type { MappingAlgorithm } from 'antd/es/theme/interface';

export interface ChartMapToken {
    defaultColor: string;
    padding?: number;
    fontFamily?: string;
    colors10: string[];
    colors20: string[];
    columnWidthRatio?: number;
    maxColumnWidth?: number;
    minColumnWidth?: number;
    roseWidthRatio?: number;
    multiplePieWidthRatio?: number;
    geometries?: object;
    components?: object;
    labels?: object;
    innerLabels?: object;
    pieLabels?: object;
    styleSheet?: object;
}

export const darkAlgorithm: MappingAlgorithm = (seedToken, mapToken) => {
    const baseToken = theme.darkAlgorithm(seedToken, mapToken);

    return {
        ...baseToken,
        colorBgElevated: '#ffffff',
        colorBgContainer: '#ffffff',
        colorBgLayout: '#ffffff',
        colorBgSpotlight: '#ffffff',
        colorBorder: '#d9d9d9',
        colorBorderSecondary: '#f0f0f0',

        colorPrimary: '#006D75',
        colorPrimaryActive: '#00474F',
        colorPrimaryHover: '#08979C',
        colorPrimaryText: '#000', // #006d75
        colorPrimaryTextActive: '#000', // #00474f
        colorPrimaryTextHover: '#000', // #157e82
        colorPrimaryBg: '#006D751A',
        colorPrimaryBgHover: '#6ca8a4',
        colorPrimaryBorder: '#4b9c99',
        colorPrimaryBorderHover: '#08979c',

        colorInfo: '#006d75',
        colorInfoActive: '#00474f',
        colorInfoHover: '#08979C',
        colorInfoText: '#006d75',
        colorInfoTextActive: '#00474f',
        colorInfoTextHover: '#157e82',
        colorInfoBg: '#006D751A',
        colorInfoBgHover: '#6ca8a4',
        colorInfoBorder: '#4b9c99',
        colorInfoBorderHover: '#08979c',

        colorSuccess: '#49aa19',
        colorSuccessActive: '#367617',
        colorSuccessHover: '#2c5816',
        colorSuccessText: '#419418',
        colorSuccessTextActive: '#367617',
        colorSuccessTextHover: '#5fa835',
        colorSuccessBg: '#151f12',
        colorSuccessBgHover: '#1b3012',
        colorSuccessBorder: '#244116',
        colorSuccessBorderHover: '#2c5816',

        colorError: '#F23C45',
        colorErrorActive: '#CF1322',
        colorErrorHover: '#FF7875',
        colorErrorText: '#F23C45',
        colorErrorTextActive: '#CF1322',
        colorErrorTextHover: '#FF7875',
        colorErrorBg: '#FFF1F0',
        colorErrorBgHover: '#FFF1F0',
        colorErrorBorder: '#ffccc7',
        colorErrorBorderHover: '#ffa39e',

        colorWarning: '#d89614',
        colorWarningActive: '#936914',
        colorWarningHover: '#6c4f14',
        colorWarningText: '#bb8314',
        colorWarningTextActive: '#936914',
        colorWarningTextHover: '#d1a235',
        colorWarningBg: '#261e11',
        colorWarningBgHover: '#3c2c11',
        colorWarningBorder: '#4f3b14',
        colorWarningBorderHover: '#6c4f14',
    };
};

export const lightAlgorithm: MappingAlgorithm = (seedToken, mapToken) => {
    const baseToken = theme.compactAlgorithm(seedToken, mapToken);

    return {
        ...baseToken,
        colorBgElevated: '#ffffff', // 彈窗背景色
        colorBgContainer: '#ffffff', // 容器背景色
        colorBgLayout: '#ffffff', // 布局背景色
        colorBgSpotlight: '#ffffff',
        colorBorder: '#d9d9d9',
        colorBorderSecondary: '#f0f0f0',

        colorPrimary: '#006D75',
        colorPrimaryActive: '#00474F',
        colorPrimaryHover: '#08979C',
        colorPrimaryText: '#000', // #006d75
        colorPrimaryTextActive: '#000', // #00474f
        colorPrimaryTextHover: '#000', // #157e82
        colorPrimaryBg: '#006D75',
        colorPrimaryBgHover: '#6ca8a4',
        colorPrimaryBorder: '#4b9c99',
        colorPrimaryBorderHover: '#08979c',

        colorInfo: '#006d75',
        colorInfoActive: '#00474f',
        colorInfoHover: '#08979C',
        colorInfoText: '#006d75',
        colorInfoTextActive: '#00474f',
        colorInfoTextHover: '#157e82',
        colorInfoBg: '#006D751A',
        colorInfoBgHover: '#6ca8a4',
        colorInfoBorder: '#4b9c99',
        colorInfoBorderHover: '#08979c',

        colorSuccess: '#49aa19',
        colorSuccessActive: '#367617',
        colorSuccessHover: '#2c5816',
        colorSuccessText: '#419418',
        colorSuccessTextActive: '#367617',
        colorSuccessTextHover: '#5fa835',
        colorSuccessBg: '#151f12',
        colorSuccessBgHover: '#1b3012',
        colorSuccessBorder: '#244116',
        colorSuccessBorderHover: '#2c5816',

        colorError: '#F23C45',
        colorErrorActive: '#CF1322',
        colorErrorHover: '#FF7875',
        colorErrorText: '#F23C45',
        colorErrorTextActive: '#CF1322',
        colorErrorTextHover: '#FF7875',
        colorErrorBg: '#FFF1F0',
        colorErrorBgHover: '#FFF1F0',
        colorErrorBorder: '#ffccc7',
        colorErrorBorderHover: '#ffa39e',

        colorWarning: '#d89614',
        colorWarningActive: '#936914',
        colorWarningHover: '#6c4f14',
        colorWarningText: '#bb8314',
        colorWarningTextActive: '#936914',
        colorWarningTextHover: '#d1a235',
        colorWarningBg: '#261e11',
        colorWarningBgHover: '#3c2c11',
        colorWarningBorder: '#4f3b14',
        colorWarningBorderHover: '#6c4f14',
    };
};

export const chartDarkAlgorithm = (): ChartMapToken => {
    return {
        defaultColor: '#138585',
        colors10: [
            '#138585',
            // '#13A8A8',
            '#58D1C9',
            // '#B2F1E8',

            '#6F9412',
            // '#8BBB11',
            '#C9E75D',
            // '#F0FAB5',

            '#D87A16',
            // '#E89A3C',
            '#F3B765',
            // '#FAE3B7',

            '#177DDC',
            // '#3C9AE8',
            '#65B7F3',
            // '#B7E3FA',

            '#2B4ACB',
            // '#5273E0',
            '#7F9EF3',
            // '#D2E0FA',
        ],
        colors20: [
            '#138585',
            '#13A8A8',
            '#58D1C9',
            '#B2F1E8',

            '#6F9412',
            '#8BBB11',
            '#C9E75D',
            '#F0FAB5',

            '#D87A16',
            '#E89A3C',
            '#F3B765',
            '#FAE3B7',

            '#177DDC',
            '#3C9AE8',
            '#65B7F3',
            '#B7E3FA',

            '#2B4ACB',
            '#5273E0',
            '#7F9EF3',
            '#D2E0FA',
        ],
        components: {
            legend: {
                common: {
                    itemName: {
                        style: {
                            fill: '#FFF',
                        },
                    },
                },
            },
        },
        innerLabels: {
            style: {
                fill: '#FFF',
            },
        },
    };
};

export const chartLightAlgorithm = (): ChartMapToken => {
    return {
        defaultColor: '#138585',
        colors10: [
            '#138585',
            // '#13A8A8',
            '#58D1C9',
            // '#B2F1E8',

            '#6F9412',
            // '#8BBB11',
            '#C9E75D',
            // '#F0FAB5',

            '#D87A16',
            // '#E89A3C',
            '#F3B765',
            // '#FAE3B7',

            '#177DDC',
            // '#3C9AE8',
            '#65B7F3',
            // '#B7E3FA',

            '#2B4ACB',
            // '#5273E0',
            '#7F9EF3',
            // '#D2E0FA',
        ],
        colors20: [
            '#138585',
            '#13A8A8',
            '#58D1C9',
            '#B2F1E8',

            '#6F9412',
            '#8BBB11',
            '#C9E75D',
            '#F0FAB5',

            '#D87A16',
            '#E89A3C',
            '#F3B765',
            '#FAE3B7',

            '#177DDC',
            '#3C9AE8',
            '#65B7F3',
            '#B7E3FA',

            '#2B4ACB',
            '#5273E0',
            '#7F9EF3',
            '#D2E0FA',
        ],
        components: {
            legend: {
                common: {
                    itemName: {
                        style: {
                            fill: '#000',
                        },
                    },
                },
            },
        },
        innerLabels: {
            style: {
                fill: '#000',
            },
        },
    };
};
